import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageBody } from "@/components/MessageBody";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, Trash2 } from "lucide-react";

const DELIVERY_LABELS = { inviata: "Inviata", consegnata: "Consegnata", aperta: "Aperta", click: "Cliccata", risposta: "Risposta", rimbalzata: "Rimbalzata", reclamo: "Reclamo", "in ritardo": "In ritardo", letto: "Letto", fallito: "Fallito" };
const deliveryClass = (s) => {
  if (["risposta", "aperta", "click", "letto"].includes(s)) return "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40";
  if (["rimbalzata", "reclamo", "fallito"].includes(s)) return "bg-red-500/20 text-red-800 dark:text-red-200 border border-red-500/40";
  if (s === "consegnata") return "bg-sky-500/20 text-sky-800 dark:text-sky-200 border border-sky-500/40";
  return "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40";
};
const batchStatusClass = (s) => (s === "inviata" ? "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40"
  : s === "parziale" ? "bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40" : "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40");
const batchStatusLabel = (s) => (s === "inviata" ? "Inviata" : s === "parziale" ? "Parziale" : "Bozza");
const fmtDateTime = (iso) => { try { return iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; } catch { return iso; } };

/** Registro delle campagne massive Email/WhatsApp (stesso archivio /campaign-batches dei Rivenditori) per un target. */
export function CampaignBatchesPanel({ target, refreshKey = 0, testidPrefix = "batches", onLoaded }) {
  const [batches, setBatches] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const T = (s) => `${testidPrefix}-${s}`;

  const load = () => api.get(`/campaign-batches?target=${target}`).then(({ data }) => { setBatches(data.batches || []); onLoaded?.((data.batches || []).length); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [target, refreshKey]);

  const open = async (b) => {
    setActive(b); setMessages([]); setLoading(true);
    try { const { data } = await api.get(`/campaign-batches/${b._id}`); setActive(data.batch); setMessages(data.messages || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  const remove = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questa campagna dal registro? Le anagrafiche e i messaggi già inviati non vengono toccati.")) return;
    try { await api.delete(`/campaign-batches/${b._id}`); toast.success("Campagna eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-2" data-testid={T("panel")}>
      <h3 className="text-sm font-semibold text-foreground">Campagne Email / WhatsApp ({batches.length})</h3>
      {batches.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-brema-card/50 p-6 text-center text-sm text-muted-foreground" data-testid={T("empty")}>
          Nessuna campagna Email/WhatsApp: seleziona più contatti e usa "Invia E-mail / WhatsApp ai selezionati".
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid={T("table")}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Campagna</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium">Canale</th>
                <th className="px-4 py-3 font-medium">Destinatari</th>
                <th className="px-4 py-3 font-medium">Inviati</th>
                <th className="px-4 py-3 font-medium">Stato</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b._id} data-testid={T("row")} onClick={() => open(b)} className="cursor-pointer border-b border-border last:border-0 hover:bg-muted">
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(b.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{b.title}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground" data-testid={T("row-kind")}>{b.kind === "single" ? "Singola" : "Massiva"}</span></td>
                  <td className="px-4 py-3">
                    {b.channel === "whatsapp"
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/15 px-2 py-0.5 text-[11px] font-medium text-[#128C7E]" data-testid={T("row-channel")}><MessageCircle className="h-3 w-3" /> WhatsApp</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground" data-testid={T("row-channel")}><Mail className="h-3 w-3" /> E-mail</span>}
                  </td>
                  <td className="px-4 py-3 text-foreground">{b.recipient_count}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">{b.sent_count || 0}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${batchStatusClass(b.status)}`}>{batchStatusLabel(b.status)}</span></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="danger" data-testid={T("delete-btn")} onClick={(e) => remove(b, e)} className="rounded-full text-xs"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-border bg-brema-card" data-testid={T("detail-dialog")}>
          <DialogHeader>
            <DialogTitle className="text-foreground">{active?.title}</DialogTitle>
            <DialogDescription>{active?.recipient_count} destinatari · {active?.sent_count || 0} inviati · creata il {fmtDateTime(active?.created_at)}</DialogDescription>
          </DialogHeader>
          {loading ? <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div> : (
            <div className="space-y-2">
              {messages.map((m) => (
                <div key={m._id} className="rounded-lg border border-border bg-brema-bg p-3" data-testid={T("message-row")}>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="font-semibold text-foreground">{m.lead_name || m.media_name || "—"} <span className="font-normal text-muted-foreground">· {m.recipient || m.wa_phone}</span></span>
                    <span className="flex items-center gap-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(m.delivery_status || (m.status === "inviata" ? "inviata" : ""))}`}>
                        {m.status === "inviata" ? (DELIVERY_LABELS[m.delivery_status] || "Inviata") : (m.delivery_status === "fallito" ? "Fallito" : "Bozza")}
                      </span>
                      {m.sent_at && <span className="text-muted-foreground">{fmtDateTime(m.sent_at)}</span>}
                    </span>
                  </div>
                  {m.subject && <p className="mt-1 text-xs font-medium text-foreground">{m.subject}</p>}
                  {m.email_body && <div className="mt-1 max-h-32 overflow-y-auto text-xs text-muted-foreground"><MessageBody text={m.email_body} /></div>}
                  {m.error && <p className="mt-1 text-xs text-red-500">{m.error}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
